import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import fr from "./locales/fr.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import ja from "./locales/ja.json";
import { makeUiTranslator } from "./translateUi";

export const LOCALES = [
  { code: "fr", label: "Français", short: "FR" },
  { code: "en", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
  { code: "ja", label: "日本語", short: "JA" },
];

const MESSAGES = { fr, en, es, ja };
const STORAGE_KEY = "woodoo_lang";
const DEFAULT_LANG = "fr";

const I18nContext = createContext(null);

function resolve(messages, key) {
  if (!key) return "";
  const parts = String(key).split(".");
  let cur = messages;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return typeof cur === "string" ? cur : undefined;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return LOCALES.some((l) => l.code === saved) ? saved : DEFAULT_LANG;
  });

  const setLang = useCallback((code) => {
    if (!LOCALES.some((l) => l.code === code)) return;
    setLangState(code);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, fallback) => {
      const fromLang = resolve(MESSAGES[lang], key);
      if (fromLang != null) {
        if (fallback != null && typeof fallback === "object" && !Array.isArray(fallback)) {
          return fromLang.replace(/\{(\w+)\}/g, (_, k) =>
            fallback[k] != null ? String(fallback[k]) : `{${k}}`
          );
        }
        return fromLang;
      }
      const fromFr = resolve(MESSAGES.fr, key);
      if (fromFr != null) {
        if (fallback != null && typeof fallback === "object" && !Array.isArray(fallback)) {
          return fromFr.replace(/\{(\w+)\}/g, (_, k) =>
            fallback[k] != null ? String(fallback[k]) : `{${k}}`
          );
        }
        return fromFr;
      }
      return fallback != null && typeof fallback !== "object" ? fallback : key;
    },
    [lang]
  );

  const ui = useMemo(() => makeUiTranslator(t), [t]);

  const value = useMemo(
    () => ({ lang, setLang, t, ui, locales: LOCALES }),
    [lang, setLang, t, ui]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

/** Shorthand — returns t() only */
export function useT() {
  return useI18n().t;
}
